import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { createContext, useContext, useState, useEffect } from 'react';

import AuthPage from './pages/AuthPage';
import SetPassword from './pages/SetPassword';
import ResetPassword from './pages/ResetPassword';
import NextShift from './pages/NextShift';
import Schedule from './pages/Schedule';
import BookShift from './pages/BookShift';
import Updates from './pages/Updates';
import Account from './pages/Account';
import AgentDashboard from './pages/AgentDashboard';
import AgentMobileDashboard from './pages/AgentMobileDashboard';
import PushPrompt from './components/PushPrompt';
import { syncPushSubscription } from './utils/push';

// ── Theme context ──────────────────────────────────────────────────────────────
export const ThemeContext = createContext({ dark: false, toggle: () => {} });
export const useTheme = () => useContext(ThemeContext);

function ThemeProvider({ children }) {
  const [dark, setDark] = useState(() => localStorage.getItem('es_theme') === 'dark');
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('es_theme', dark ? 'dark' : 'light');
  }, [dark]);
  return (
    <ThemeContext.Provider value={{ dark, toggle: () => setDark(d => !d) }}>
      {children}
    </ThemeContext.Provider>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  // Step 3.5 Pass B: the mobile/desktop split used to be read ONCE at first
  // render — a tablet that loaded in portrait (<768px on many Android tablets)
  // stayed on the mobile layout even after rotating to landscape, and vice
  // versa. Now it re-evaluates live on resize/rotation. (Audit item #24.)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isMobileDevice = isIOS || isAndroid;

  // Session AH: re-point any existing browser push subscription at whoever is
  // signed in now. ⚠️ Must be declared HERE, with the other hooks, not inside
  // the staff branch below — AppRoutes returns early in five places and a hook
  // after a conditional return breaks the rules of hooks. The user check lives
  // inside the effect instead.
  useEffect(() => {
    if (user && user.role === 'staff' && !user.must_change_password) {
      syncPushSubscription();
    }
  }, [user]);

  // Session AG: /reset/<token> from the forgotten-password email. Checked on
  // the pathname rather than added to <Routes> below, because <Routes> is only
  // reached by a logged-in STAFF user — everyone else returns above it, so a
  // Route here would never render. Same reason /join/ is handled this way.
  // Placed above the loading check deliberately: the reset screen needs no
  // session, so it should not wait on the /me round trip. Placed above the
  // logged-in branches too, so an already-signed-in person on a shared phone
  // still lands on the reset form rather than a dashboard.
  if (window.location.pathname.startsWith('/reset/')) return <ResetPassword />;

  if (loading) return <div className="loading" style={{ paddingTop: '40vh' }}>Loading...</div>;
  if (!user && window.location.pathname.startsWith('/join/')) return <AuthPage />;
  if (!user) return <AuthPage />;

  // First-login lock (bulk-imported users): the backend 403s every route
  // except set-first-password and /me while this flag is set, so there is
  // nothing else to render. Sits before the pending screen deliberately —
  // password + POPIA consent come before anything else.
  if (user.must_change_password) return <SetPassword />;

  if (user.role === 'staff' && user.status === 'pending') return (
    <div className="auth-screen">
      <div className="auth-box">
        <div className="auth-logo">
          <h1>
            <span style={{color:'var(--text-primary)',fontWeight:800}}>{process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}</span>
            <span style={{color:'var(--accent)',fontWeight:800}}>{process.env.REACT_APP_BRAND_SUFFIX||'on'}</span>
          </h1>
        </div>
        <div style={{textAlign:'center',padding:'8px 0 24px'}}>
          <div style={{fontSize:36,marginBottom:16}}>⏳</div>
          <div style={{fontWeight:700,fontSize:17,marginBottom:10,color:'var(--text-primary)'}}>Pending Approval</div>
          <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.6,marginBottom:24}}>
            Your account is awaiting approval from your coordinator. You'll get access to shifts once approved.
          </div>

          {isMobileDevice && (
            <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px',marginBottom:20,textAlign:'left'}}>
              <div style={{fontWeight:700,fontSize:13,color:'var(--text-primary)',marginBottom:6}}>📲 Install the {`${process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}${process.env.REACT_APP_BRAND_SUFFIX||'on'}`} app</div>
              {isIOS ? (
                <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.7}}>
                  Tap the <strong>Share button</strong> (box with arrow) at the bottom of your browser, then tap <strong>"Add to Home Screen"</strong>.
                </div>
              ) : (
                <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.7}}>
                  Tap the <strong>three dots menu</strong> in your browser, then tap <strong>"Add to Home screen"</strong>.
                </div>
              )}
            </div>
          )}

          <button className="btn btn-primary" onClick={() => { localStorage.removeItem('es_token'); window.location.reload(); }}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );

  // Session AH: the push opt-in nudge. Inside the STAFF branch only — agents
  // live on the dashboard and were deliberately left out of the first push
  // rollout. The component renders nothing unless the app is running installed,
  // permission is still unasked, and the prompt hasn't been dismissed before.
  if (user.role === 'agent') return isMobile ? <AgentMobileDashboard /> : <AgentDashboard />;
  return (
    <>
    <PushPrompt />
    <Routes>
      <Route path="/" element={<NextShift />} />
      <Route path="/schedule" element={<Schedule />} />
      <Route path="/book" element={<BookShift />} />
      <Route path="/updates" element={<Updates />} />
      <Route path="/account" element={<Account />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
